import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { User, Building2, Briefcase, ArrowRight, CheckCircle2, Circle, MousePointerClick } from "lucide-react";
import { createAssessment, saveResume } from "@/lib/api";
import { trackEvent } from "@/lib/metaPixel";

const MODES = [
  {
    id: "solopreneur",
    label: "Solopreneur",
    icon: User,
    blurb: "You run the business yourself. Continuity often lives in your head.",
    risks: ["Key-person dependency", "Undocumented processes", "Single device or laptop"],
    focus: ["Personal redundancy", "Lightweight runbooks", "Client communications"],
  },
  {
    id: "sme",
    label: "Small / Medium Enterprise",
    icon: Building2,
    blurb: "A team of operators. Practical continuity that can actually be executed.",
    risks: ["Supplier concentration", "Single site dependency", "Cash-flow disruption"],
    focus: ["Vendor recovery", "Staff cross-training", "Customer continuity"],
  },
  {
    id: "corporate",
    label: "Corporate Department",
    icon: Briefcase,
    blurb: "Departmental ownership inside a larger organisation. Stakeholder-aware.",
    risks: ["Cross-team handoffs", "Limited decision authority", "Stakeholder communication gaps"],
    focus: ["Service continuity", "Governance & escalation", "Stakeholder reporting"],
  },
];

const ORG_SIZE_OPTIONS = [
  { value: "solo", label: "Just me (1)" },
  { value: "2_10", label: "Small team (2–10)" },
  { value: "11_50", label: "Growing (11–50)" },
  { value: "51_250", label: "Mid-market (51–250)" },
  { value: "250_plus", label: "Enterprise (250+)" },
];

// Curated industry options — the LLM still receives whatever the user picks
// (or types under "Other"), and the sector_presets module does keyword-based
// matching server-side to attach industry-specific risk hints automatically.
const INDUSTRY_OPTIONS = [
  "Personal Care (salon / spa / beauty)",
  "Hospitality (hotel / restaurant / bar / events)",
  "FMCG (food / beverage / consumer goods)",
  "Health Care (clinic / allied health)",
  "Distribution / Retail (physical / e-commerce / wholesale)",
  "Consulting / Professional Services",
  "Manufacturing (light / fabrication / food production)",
  "Technology / SaaS",
  "Financial Services",
  "Construction / Trades",
  "Education / Training",
  "Other (please specify)",
];
const INDUSTRY_OTHER = "Other (please specify)";

// Curated country list — pickable plus an "Other" fallback for anything else.
// Sent to the LLM verbatim, which uses it (plus the current UTC month) to surface
// region-specific seasonal natural-hazard risks (hurricane / avalanche / monsoon
// / typhoon / wildfire etc.).
const COUNTRY_OPTIONS = [
  "United States", "Canada", "United Kingdom", "Ireland",
  "Jamaica", "Bahamas", "Trinidad and Tobago", "Barbados", "Dominican Republic", "Puerto Rico",
  "Switzerland", "Germany", "France", "Italy", "Spain", "Portugal", "Netherlands", "Belgium",
  "Austria", "Sweden", "Norway", "Denmark", "Finland", "Poland",
  "Australia", "New Zealand",
  "South Africa", "Nigeria", "Kenya", "Ghana",
  "United Arab Emirates", "Saudi Arabia",
  "India", "Pakistan", "Bangladesh", "Sri Lanka",
  "Singapore", "Malaysia", "Indonesia", "Philippines", "Thailand", "Vietnam",
  "Japan", "South Korea", "Hong Kong", "Taiwan",
  "China",
  "Brazil", "Mexico", "Argentina", "Colombia", "Chile", "Peru",
  "Other (please specify)",
];
const COUNTRY_OTHER = "Other (please specify)";

export default function ModeSelect() {
  const [selected, setSelected] = useState(null);
  const [organization, setOrganization] = useState("");
  const [email, setEmail] = useState("");
  const [industryChoice, setIndustryChoice] = useState("");
  const [industryOther, setIndustryOther] = useState("");
  const [countryChoice, setCountryChoice] = useState("");
  const [countryOther, setCountryOther] = useState("");
  const [department, setDepartment] = useState("");
  const [orgSize, setOrgSize] = useState("");
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const advisorMode = searchParams.get("advisor") === "1";
  const [advisorFirm, setAdvisorFirm] = useState("");
  const [clientName, setClientName] = useState("");
  const navigate = useNavigate();

  // Resolve the effective industry value sent to the backend.
  const industry = industryChoice === INDUSTRY_OTHER
    ? industryOther.trim()
    : industryChoice;
  const country = countryChoice === COUNTRY_OTHER
    ? countryOther.trim()
    : countryChoice;

  const start = async () => {
    if (!selected) {
      toast.error("Please select a mode to continue.");
      return;
    }
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address (or leave it blank).");
      return;
    }
    try {
      setBusy(true);
      const a = await createAssessment(
        selected,
        organization || undefined,
        email || undefined,
        {
          industry: industry || undefined,
          department: department || undefined,
          org_size: orgSize || undefined,
          country: country || undefined,
          is_advisor: advisorMode || undefined,
          advisor_firm: advisorMode ? (advisorFirm.trim() || undefined) : undefined,
          client_name: advisorMode ? (clientName.trim() || undefined) : undefined,
        },
      );
      saveResume({ id: a.id, mode: a.mode, status: a.status });
      try {
        const modeLabel = (MODES.find((m) => m.id === selected) || {}).label || selected;
        trackEvent("StartTrial", { content_name: modeLabel, status: "assessment_started" });
      } catch (err) { /* never block the flow on analytics */ }
      navigate(`/assessment/${a.id}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not start assessment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700">Step 1 of 3</p>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              How do you operate?
            </h1>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Your selection shapes the questioning style, operational focus, and recommendations.
            </p>
          </div>

          <div className="mt-8 flex items-center gap-2 text-sm">
            <span
              data-testid="mode-pick-instruction"
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12.5px] font-semibold transition-colors ${
                selected
                  ? "border-teal-200 bg-teal-50 text-teal-800"
                  : "border-amber-300 bg-amber-50 text-slate-800"
              }`}
            >
              {selected ? (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Mode selected — fill the details below, then begin
                </>
              ) : (
                <>
                  <MousePointerClick className="h-4 w-4 animate-pulse" />
                  Pick one option below to continue
                </>
              )}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = selected === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setSelected(m.id)}
                  data-testid={`mode-${m.id}-btn`}
                  aria-pressed={active}
                  className={`relative text-left rounded-lg border bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-[2px] hover:shadow-md ${
                    active
                      ? "border-teal-600 ring-2 ring-teal-500/15"
                      : selected
                        ? "border-slate-200 hover:border-slate-300"
                        : "border-slate-200 hover:border-slate-300 ring-1 ring-amber-200/60"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    data-testid={`mode-${m.id}-checkbox`}
                    className={`absolute top-4 right-4 inline-flex items-center justify-center h-6 w-6 rounded-full border-2 transition-colors ${
                      active
                        ? "border-teal-600 bg-teal-600 text-white"
                        : "border-slate-300 bg-white text-slate-300"
                    }`}
                  >
                    {active ? <CheckCircle2 className="h-5 w-5" strokeWidth={2.4} /> : <Circle className="h-3 w-3 fill-current" />}
                  </span>

                  <div className="flex items-center justify-between pr-10">
                    <div className={`h-9 w-9 rounded-md flex items-center justify-center ${active ? "bg-teal-600 text-white" : "bg-slate-900 text-white"}`}>
                      <Icon className="h-4 w-4" strokeWidth={2.2} />
                    </div>
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{m.label}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{m.blurb}</p>

                  <div className="mt-5 space-y-3 text-[12.5px]">
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500">Example risks</p>
                      <ul className="mt-1 list-disc list-inside text-slate-600 space-y-0.5">
                        {m.risks.map((r) => <li key={r}>{r}</li>)}
                      </ul>
                    </div>
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500">Focus areas</p>
                      <ul className="mt-1 list-disc list-inside text-slate-600 space-y-0.5">
                        {m.focus.map((r) => <li key={r}>{r}</li>)}
                      </ul>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-10 max-w-2xl">
            {advisorMode && (
              <div className="mb-6 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/70 dark:bg-teal-900/20 p-4" data-testid="advisor-handover-panel">
                <p className="text-[11px] uppercase tracking-[0.14em] text-teal-700 dark:text-teal-400">Advisor hand-over</p>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">Running this on behalf of a client? Add their details — they print on the readiness brief cover.</p>
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="client-name">Client name</label>
                    <input
                      id="client-name"
                      data-testid="advisor-client-name"
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="e.g. Acme Manufacturing Ltd"
                      className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="advisor-firm">Your firm</label>
                    <input
                      id="advisor-firm"
                      data-testid="advisor-firm"
                      type="text"
                      value={advisorFirm}
                      onChange={(e) => setAdvisorFirm(e.target.value)}
                      placeholder="e.g. Northstar Advisory"
                      className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                    />
                  </div>
                </div>
              </div>
            )}
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Tell us a bit about you</p>
            <p className="mt-1 text-xs text-slate-500">Everything below is optional. Even a couple of fields meaningfully sharpens the assessment — especially the industry and team size.</p>

            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="org">
                  Organisation name
                </label>
                <input
                  id="org"
                  data-testid="org-input"
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="e.g. Northwind Logistics"
                  className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="industry">
                  Industry / sector
                </label>
                <select
                  id="industry"
                  data-testid="industry-select"
                  value={industryChoice}
                  onChange={(e) => setIndustryChoice(e.target.value)}
                  className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                >
                  <option value="">— Select an industry —</option>
                  {INDUSTRY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {industryChoice === INDUSTRY_OTHER && (
                  <input
                    data-testid="industry-other-input"
                    type="text"
                    value={industryOther}
                    onChange={(e) => setIndustryOther(e.target.value)}
                    placeholder="Describe your industry (e.g. Logistics, Energy, Agritech)"
                    className="mt-2 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                  />
                )}
                <p className="mt-1 text-[11px] text-slate-500">Listed sectors get extra industry-specific risk scenarios automatically. Pick "Other" for anything else.</p>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="country">
                  Country / region
                </label>
                <select
                  id="country"
                  data-testid="country-select"
                  value={countryChoice}
                  onChange={(e) => setCountryChoice(e.target.value)}
                  className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                >
                  <option value="">— Select a country —</option>
                  {COUNTRY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {countryChoice === COUNTRY_OTHER && (
                  <input
                    data-testid="country-other-input"
                    type="text"
                    value={countryOther}
                    onChange={(e) => setCountryOther(e.target.value)}
                    placeholder="Type your country / region"
                    className="mt-2 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                  />
                )}
                <p className="mt-1 text-[11px] text-slate-500">Country shapes seasonal risks — hurricane (Caribbean Jun–Nov), avalanche (Alpine Dec–Mar), monsoon, typhoon, wildfire, etc.</p>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="org-size">
                  Organisation size
                </label>
                <select
                  id="org-size"
                  data-testid="org-size-input"
                  value={orgSize}
                  onChange={(e) => setOrgSize(e.target.value)}
                  className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                >
                  <option value="">— Select —</option>
                  {ORG_SIZE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {selected === "corporate" && (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="department">
                    Department
                  </label>
                  <input
                    id="department"
                    data-testid="department-input"
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="e.g. Finance, Operations, IT, HR"
                    className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                  />
                </div>
              )}

              <div className={selected === "corporate" ? "sm:col-span-2" : ""}>
                <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500" htmlFor="email">
                  Your email
                </label>
                <input
                  id="email"
                  data-testid="modeselect-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
                />
                <p className="mt-1 text-xs text-slate-500">Lets us email you the plan. Never shared.</p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-3">
            <button
              onClick={start}
              disabled={!selected || busy}
              data-testid="continue-btn"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Preparing assessment…" : "Begin Assessment"} <ArrowRight className="h-4 w-4" />
            </button>
            <span className="text-xs text-slate-500">
              {selected
                ? "You can change your answers anytime before exporting."
                : "Select a business type above to enable this button."}
            </span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
